import gettext from '@eb/gettext';
import { REGISTRATION, TICKETING } from '../constants';
import { DeliveryMethod } from '../api/models/deliveryMethod';

/**
 * Used to provide the correct strings for both ticketing and registration events.
 * When adding a new string that uses ticketing terminology, you must add a
 * variation for both ticketing and registration.
 * @type {Object}
 */
export const TERMINOLOGY = {
    [TICKETING]: {
        tickets: gettext('Tickets'),
        /* Translators: For orders with multiple tickets, this is the ticket number. Example: Ticket 2 */
        attendeeSectionTitle: (ticketNumber) =>
            gettext('Ticket %(ticketNumber)s', { ticketNumber }),
        viewTickets: gettext('View Tickets'),
        deliveryMethodDisplays: {
            [DeliveryMethod.eTicket]: gettext('eTicket'),
            [DeliveryMethod.willCall]: gettext('Will Call'),
            [DeliveryMethod.standardShipping]: gettext('Standard Shipping'),
        },
        deliveryMethodInstruction: {
            [DeliveryMethod.eTicket]: gettext(`
                Your ticket will be attached to your confirmation email. You can
                also access it from your Eventbrite account or mobile app.
            `),
            [DeliveryMethod.willCall]: gettext(`
                Your ticket will be waiting for you at the venue's pick-up location
                on the day of the event. Please bring photo ID and the card used for purchase.
            `),
            [DeliveryMethod.standardShipping]: gettext(`
                It should arrive no later than 48 hours before the event.
            `),
        },
        deliveryMethodInstructions: {
            [DeliveryMethod.eTicket]: gettext(`
                Your tickets will be attached to your confirmation email. You can
                also access them from your Eventbrite account or mobile app.
            `),
            [DeliveryMethod.willCall]: gettext(`
                Your tickets will be waiting for you at the venue's pick-up location
                on the day of the event. Please bring photo ID and the card used for purchase.
            `),
            [DeliveryMethod.standardShipping]: gettext(`
                They should arrive no later than 48 hours before the event.
            `),
        },
        deliveryMethodConfirmationHeaders: {
            // Translators: This shows how many electronic tickets were purchased.
            // For example: "5 TICKETS SENT TO buyer123@eventbrite.com"
            ticketSentTo: gettext('Ticket sent to'),
            ticketsSentTo: gettext('Tickets sent to'),
            // Translators: This shows how many will call tickets were purchased.
            // For example: "5 TICKETS AT WILL CALL"
            ticketAtWillCall: gettext('Ticket at Will Call'),
            ticketsAtWillCall: gettext('Tickets at Will Call'),
            ticketShipped: gettext('Ticket shipping to'),
            ticketsShipped: gettext('Tickets shipping to'),
        },
        accessCodeSuccessMessages: {
            // Translators: This code gives access to hidden tickets, not a discount on tickets.
            access: gettext(
                '%(promoCodeHtml)s applied. New ticket unlocked below.',
            ),
            // Translators: This code gives access to hidden tickets, not a discount on tickets.
            accessPlural: gettext(
                '%(promoCodeHtml)s applied. New tickets unlocked below.',
            ),
        },
        waitlistInstructions: gettext(`
            Ticket availability is determined by the event organizer. Being added to the waitlist does
            not guarantee that you will be able to purchase a ticket in the future. Each waitlist entry only
            allows you to purchase an individual ticket when the event organizer releases the ticket.
        `),
        ticketCategoryTitle: {
            admission: gettext('Tickets'),
            donation: gettext('Donations'),
            addon: gettext('Addons'),
        },
        seriesTickets: gettext('Tickets'),
    },
    [REGISTRATION]: {
        tickets: gettext('Registrations'),
        /* Translators: For orders with multiple tickets, this is the ticket number. Example: Ticket 2 */
        attendeeSectionTitle: (ticketNumber) =>
            gettext('Registration %(ticketNumber)s', { ticketNumber }),
        viewTickets: gettext('View Registrations'),
        deliveryMethodDisplays: {
            [DeliveryMethod.eTicket]: gettext('eRegistration'),
            [DeliveryMethod.willCall]: gettext('Will Call'),
            [DeliveryMethod.standardShipping]: gettext('Standard Shipping'),
        },
        deliveryMethodInstruction: {
            [DeliveryMethod.eTicket]: gettext(`
                Your registration will be attached to your confirmation email. You can
                also access it from your Eventbrite account or mobile app.
            `),
            [DeliveryMethod.willCall]: gettext(`
                Your registration will be waiting for you at the venue's pick-up location
                on the day of the event. Please bring photo ID and the card used for purchase.
            `),
            [DeliveryMethod.standardShipping]: gettext(`
                It should arrive no later than 48 hours before the event.
            `),
        },
        deliveryMethodInstructions: {
            [DeliveryMethod.eTicket]: gettext(`
                Your registrations will be attached to your confirmation email. You can
                also access them from your Eventbrite account or mobile app.
            `),
            [DeliveryMethod.willCall]: gettext(`
                Your registrations will be waiting for you at the venue's pick-up location
                on the day of the event. Please bring photo ID and the card used for purchase.
            `),
            [DeliveryMethod.standardShipping]: gettext(`
                They should arrive no later than 48 hours before the event.
            `),
        },
        deliveryMethodConfirmationHeaders: {
            // Translators: This shows how many electronic registrations were purchased.
            // For example: "5 REGISTRATIONS SENT TO buyer123@eventbrite.com"
            ticketSentTo: gettext('Registration sent to'),
            ticketsSentTo: gettext('Registrations sent to'),
            // Translators: This shows how many will call registrations were purchased.
            // For example: "5 REGISTRATIONS AT WILL CALL"
            ticketAtWillCall: gettext('Registration at Will Call'),
            ticketsAtWillCall: gettext('Registrations at Will Call'),
            ticketShipped: gettext('Registration shipping to'),
            ticketsShipped: gettext('Registrations shipping to'),
        },
        accessCodeSuccessMessages: {
            // Translators: This code gives access to hidden registrations, not a discount on registration.
            access: gettext(
                '%(promoCodeHtml)s applied. New registration unlocked below.',
            ),
            // Translators: This code gives access to hidden registrations, not a discount on registration.
            accessPlural: gettext(
                '%(promoCodeHtml)s applied. New registrations unlocked below.',
            ),
        },
        waitlistInstructions: gettext(`
            Registration availability is determined by the event organizer. Being added to the waitlist does
            not guarantee that you will be able to purchase a registration in the future. Each waitlist entry only
            allows you to purchase an individual registration when the event organizer releases the registration.
        `),
        ticketCategoryTitle: {
            admission: gettext('Registration'),
            donation: gettext('Donations'),
            addon: gettext('Addons'),
        },
        seriesTickets: gettext('Register'),
    },
};

export const PRIVACY_MESSAGE_ADDING_ATTENDEES = gettext(
    'As a reminder, the creator is responsible for compliance with privacy and marketing regulation when using this feature to upload email addresses for marketing communications',
);
